import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { SearchItem, SearchSources } from "@/shared/components/GlobalSearch";
import { navSections } from "@/portals/clinician/layout/ClinicianSidebar";
import { myItemsQuery, queueQuery } from "@/portals/clinician/api/clinicianQueries";
import { ISSUE_LABELS } from "@/features/patient/medications/components/medicationReviewBadges";

const join = (...parts: (string | null | undefined | false)[]) => parts.filter(Boolean).join(" · ");

/**
 * The clinician header search: their pages, the items they hold (My Items)
 * and the shared review queue — by medicine and issue (clinicians never see
 * patient names). Runs only while the search dropdown is open.
 */
export function useClinicianSearch(): SearchSources {
  const mine = useQuery(myItemsQuery());
  const queue = useQuery(queueQuery());

  const items = useMemo<SearchItem[]>(() => {
    const out: SearchItem[] = [];
    for (const section of navSections) {
      for (const page of section.items) {
        out.push({ id: page.path, group: "Pages", title: page.label, subtitle: section.title, icon: page.icon, to: page.path });
      }
    }
    for (const m of mine.data ?? []) {
      out.push({
        id: m.id, group: "My items", title: m.medicineName || ISSUE_LABELS[m.triggerType] || "Review item", icon: "lucide:user-check",
        subtitle: join(ISSUE_LABELS[m.triggerType], m.patientAge && `Patient, age ${m.patientAge}`, m.awaitingPatientReply && "Waiting for the patient"),
        keywords: "mine held", to: `/clinician/my-items/${encodeURIComponent(m.id)}`,
      });
    }
    for (const q of queue.data ?? []) {
      out.push({
        id: q.id, group: "Review queue", title: q.medicineName || ISSUE_LABELS[q.triggerType] || "Review item", icon: "lucide:clipboard-list",
        subtitle: join(ISSUE_LABELS[q.triggerType], `${q.recordCount} ${q.recordCount === 1 ? "record" : "records"}`),
        keywords: "queue unclaimed", to: `/clinician/queue/${encodeURIComponent(q.id)}`,
      });
    }
    return out;
  }, [mine.data, queue.data]);

  return { items, loading: mine.isPending || queue.isPending };
}
